/**
 * NewNotificationDrawer
 * ---------------------
 * The full "Notification Center" that opens from the navbar bell. This is the
 * NEW redesigned drawer (Replit-style: stat cards + segmented tabs + rich
 * cards). The legacy `NotificationDrawer` folder is intentionally left in place
 * but is no longer rendered — keep this one as the source of truth.
 *
 * Data — all real, no dummy:
 *   - orders        → live order list (getAllOrderlist) mapped via buildOrderNotifs.
 *   - waiterTasks   → restaurant table requests (polled by tableTaskPollingService).
 *   - table pins    → fetched on demand when the "Table Pins" tab is opened.
 *
 * Read state is OWNED by the parent (RestaurantNotificationCenter) and passed in
 * (readIds + onMarkRead + onMarkAllRead) so the drawer and the dynamic island
 * share one persisted read state — marking read in either place clears the
 * island's red dot. See posnavbar/index.js.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Drawer } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";

import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import TableRestaurantOutlinedIcon from "@mui/icons-material/TableRestaurantOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getAllTablePins } from "../../../../../config/Services";
import { refreshTableTasks } from "../../../../../utils/notificationSystem/tableTaskPollingService";
import {
  buildOrderNotifs,
  mapWaiterTasksToNotifs,
  sortByRecent,
  TASK_TYPE_ICONS,
  TASK_TYPE_COLORS,
} from "../../../../../utils/notificationSystem/notificationMappers";
import "./NewNotificationDrawer.css";

// ── Visuals per order-notification type ───────────────────────────────────────
const ORDER_VISUAL = {
  NEW_ORDER: { bg: "#dbeafe", color: "#2563eb", Icon: ShoppingBagOutlinedIcon, cat: "Order" },
  KITCHEN_ORDER: {
    bg: "#fff1dc",
    color: "#ad6417",
    Icon: RestaurantOutlinedIcon,
    cat: "Kitchen",
  },
  NEW_DELIVERY: { bg: "#ede9fe", color: "#7c3aed", Icon: LocalShippingOutlinedIcon, cat: "Online" },
  ORDER_ACCEPTED: { bg: "#e0f2fe", color: "#0284c7", Icon: CheckCircleOutlinedIcon, cat: "Order" },
  ORDER_PREPARING: { bg: "#fef3c7", color: "#d97706", Icon: AccessTimeOutlinedIcon, cat: "Order" },
  ORDER_READY: { bg: "#dcfce7", color: "#16a34a", Icon: RestaurantOutlinedIcon, cat: "Order" },
  OUT_FOR_DELIVERY: {
    bg: "#e0e7ff",
    color: "#4f46e5",
    Icon: LocalShippingOutlinedIcon,
    cat: "Delivery",
  },
  PAYMENT_RECEIVED: {
    bg: "#dcfce7",
    color: "#16a34a",
    Icon: CreditCardOutlinedIcon,
    cat: "Payment",
  },
};

const PRIORITY = {
  ORDER_READY: "high",
  NEW_DELIVERY: "high",
  TABLE_TASK: "high",
  KITCHEN_ORDER: "high",
  NEW_ORDER: "medium",
  ORDER_PREPARING: "medium",
  OUT_FOR_DELIVERY: "medium",
  ORDER_ACCEPTED: "low",
  PAYMENT_RECEIVED: "low",
};

const PIN_STATUS = {
  AUTHORIZED: { label: "Active", bg: "#dcfce7", color: "#16a34a" },
  AUTHORIZATION_PENDING: { label: "Pending", bg: "#fef3c7", color: "#d97706" },
  AUTHORIZED_CLOSED: { label: "Closed", bg: "#fee2e2", color: "#dc2626" },
};

const getVisual = (n) => {
  if (n.type === "TABLE_TASK") {
    const color = TASK_TYPE_COLORS[n.taskAction] || TASK_TYPE_COLORS.DEFAULT;
    return {
      bg: `${color}22`,
      color,
      emoji: TASK_TYPE_ICONS[n.taskAction] || TASK_TYPE_ICONS.DEFAULT,
      cat: "Table Request",
    };
  }
  return ORDER_VISUAL[n.type] || ORDER_VISUAL.NEW_ORDER;
};

const cardLabel = (n) => {
  const tag = n.tags?.[0];
  return tag ? `${n.title} — ${tag}` : n.title;
};

const timeAgo = (d) => {
  if (!d) return "";
  const t = new Date(d).getTime();
  if (Number.isNaN(t)) return "";
  const m = Math.floor((Date.now() - t) / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.floor(h / 24)} d ago`;
};

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const NewNotificationDrawer = ({
  open,
  onClose,
  orders = [],
  retailType,
  waiterTasks = [],
  additionalKitchenNotifications = [],
  tasksLoading = false,
  readIds = new Set(),
  onMarkRead = () => {},
  onMarkAllRead = () => {},
}) => {
  const isRestaurant = retailType === "RESTAURANT";
  const navigate = useNavigate();

  const [category, setCategory] = useState("activity"); // activity | tasks | pins
  const [readFilter, setReadFilter] = useState("all"); // all | unread | read
  const [search, setSearch] = useState("");
  const [tablePins, setTablePins] = useState([]);
  const [pinsLoading, setPinsLoading] = useState(false);

  const orderNotifs = useMemo(
    () => sortByRecent([...buildOrderNotifs(orders), ...additionalKitchenNotifications]),
    [orders, additionalKitchenNotifications]
  );
  const taskNotifs = useMemo(
    () => (isRestaurant ? sortByRecent(mapWaiterTasksToNotifs(waiterTasks)) : []),
    [waiterTasks, isRestaurant]
  );
  const allNotifs = useMemo(() => [...orderNotifs, ...taskNotifs], [orderNotifs, taskNotifs]);

  const isUnread = (n) => !readIds.has(n.id);
  const unreadCount = allNotifs.filter(isUnread).length;

  // ── Stat cards (derived from real data) ──
  const stats = isRestaurant
    ? [
        {
          key: "unread",
          label: "Unread",
          sub: "Needs attention",
          value: unreadCount,
          icon: <NotificationsNoneOutlinedIcon />,
          bg: "#dbeafe",
          color: "#2563eb",
          onClick: () => {
            setCategory("activity");
            setReadFilter("unread");
          },
        },
        {
          key: "new",
          label: "New Orders",
          sub: "Just came in",
          value: orderNotifs.filter((n) => ["NEW_ORDER", "NEW_DELIVERY", "KITCHEN_ORDER"].includes(n.type))
            .length,
          icon: <ShoppingBagOutlinedIcon />,
          bg: "#fef3c7",
          color: "#d97706",
          onClick: () => {
            setCategory("activity");
            setReadFilter("all");
          },
        },
        {
          key: "ready",
          label: "Ready to Serve",
          sub: "Service-impacting",
          value: orderNotifs.filter((n) => n.type === "ORDER_READY").length,
          icon: <RestaurantOutlinedIcon />,
          bg: "#dcfce7",
          color: "#16a34a",
          onClick: () => {
            setCategory("activity");
            setReadFilter("all");
          },
        },
        {
          key: "tasks",
          label: "Table Requests",
          sub: "Guests waiting",
          value: taskNotifs.length,
          icon: <CampaignOutlinedIcon />,
          bg: "#f3e8ff",
          color: "#7c3aed",
          onClick: () => setCategory("tasks"),
        },
      ]
    : [
        {
          key: "unread",
          label: "Unread",
          sub: "Needs attention",
          value: unreadCount,
          icon: <NotificationsNoneOutlinedIcon />,
          bg: "#dbeafe",
          color: "#2563eb",
          onClick: () => setReadFilter("unread"),
        },
        {
          key: "new",
          label: "New Orders",
          sub: "Just came in",
          value: orderNotifs.filter((n) => ["NEW_ORDER", "NEW_DELIVERY", "KITCHEN_ORDER"].includes(n.type))
            .length,
          icon: <ShoppingBagOutlinedIcon />,
          bg: "#fef3c7",
          color: "#d97706",
          onClick: () => setReadFilter("all"),
        },
        {
          key: "ready",
          label: "Ready",
          sub: "Ready to collect",
          value: orderNotifs.filter((n) => n.type === "ORDER_READY").length,
          icon: <RestaurantOutlinedIcon />,
          bg: "#dcfce7",
          color: "#16a34a",
          onClick: () => setReadFilter("all"),
        },
        {
          key: "paid",
          label: "Payments",
          sub: "Completed",
          value: orderNotifs.filter((n) => n.type === "PAYMENT_RECEIVED").length,
          icon: <CreditCardOutlinedIcon />,
          bg: "#dcfce7",
          color: "#16a34a",
          onClick: () => setReadFilter("all"),
        },
      ];

  const categories = isRestaurant
    ? [
        { id: "activity", label: "Activity" },
        { id: "tasks", label: "Table Requests" },
        { id: "pins", label: "Table Pins" },
      ]
    : [];

  // ── Fetch table pins on demand ──
  useEffect(() => {
    if (!open || category !== "pins") return;
    let active = true;
    (async () => {
      try {
        setPinsLoading(true);
        const res = await getAllTablePins({
          organisationId: localStorage.getItem("orgId"),
          locationId: localStorage.getItem("locId"),
          status: ["AUTHORIZATION_PENDING", "AUTHORIZED"],
          page: 0,
          size: 50,
        });
        if (active && res?.data?.content) setTablePins(res.data.content);
      } catch {
        /* ignore */
      } finally {
        if (active) setPinsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, category]);

  // Fetch fresh table requests on demand when the tab is opened, instead of
  // waiting for the 3-minute poll. The poller notifies its subscribers, so the
  // updated list flows back down through the waiterTasks prop.
  useEffect(() => {
    if (!open || category !== "tasks" || !isRestaurant) return;
    refreshTableTasks();
  }, [open, category, isRestaurant]);

  // Reset to a valid category when retail type changes.
  useEffect(() => {
    if (!isRestaurant) setCategory("activity");
  }, [isRestaurant]);

  const applyFilters = (list) => {
    let out = list;
    if (readFilter === "unread") out = out.filter(isUnread);
    else if (readFilter === "read") out = out.filter((n) => !isUnread(n));
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter(
        (n) =>
          n.title?.toLowerCase().includes(q) ||
          n.description?.toLowerCase().includes(q) ||
          n.tags?.some((t) => String(t).toLowerCase().includes(q))
      );
    }
    return out;
  };

  const visibleList = category === "tasks" ? applyFilters(taskNotifs) : applyFilters(orderNotifs);

  const handleViewDetails = (n) => {
    onMarkRead(n.id);
    onClose();
    if (n.type === "TABLE_TASK") navigate("/products/table-layout");
    else if (n.filterType === "delivery") navigate("/orders");
    else navigate(isRestaurant ? "/products/table-layout" : "/orders");
  };

  const handleMarkAll = () => onMarkAllRead(allNotifs.map((n) => n.id));

  const handleSupport = () => {
    window.dispatchEvent(new CustomEvent("pos:open-help-and-support"));
    onClose();
  };

  const renderCard = (n) => {
    const v = getVisual(n);
    const AvatarIcon = v.Icon;
    const read = !isUnread(n);
    const prio = PRIORITY[n.type];
    return (
      <div key={n.id} className={`ncd-card ${read ? "read" : ""}`} onClick={() => onMarkRead(n.id)}>
        {!read && <span className="ncd-card-unread" />}
        <div className="ncd-card-avatar" style={{ background: v.bg, color: v.color }}>
          {v.emoji ? (
            <span className="ncd-emoji">{v.emoji}</span>
          ) : (
            <AvatarIcon sx={{ fontSize: "1.3rem" }} />
          )}
        </div>
        <div className="ncd-card-main">
          <div className="ncd-card-chips">
            <span className="ncd-chip" style={{ color: v.color, background: v.bg }}>
              {v.cat}
            </span>
            {prio && <span className={`ncd-chip ncd-prio-${prio}`}>{prio} priority</span>}
            {!read && <span className="ncd-chip ncd-chip-new">New</span>}
          </div>
          <h4 className="ncd-card-title">{cardLabel(n)}</h4>
          <p className="ncd-card-desc">{n.description}</p>
          <div className="ncd-card-footer">
            <span className="ncd-card-meta">{(n.tags || []).join("   ·   ")}</span>
            <button
              type="button"
              className="ncd-card-link"
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails(n);
              }}
            >
              View details <ChevronRightIcon sx={{ fontSize: "1rem" }} />
            </button>
          </div>
        </div>
        <div className="ncd-card-time">
          <AccessTimeOutlinedIcon sx={{ fontSize: "0.85rem" }} /> {timeAgo(n.createdAt)}
        </div>
      </div>
    );
  };

  const renderPins = () => {
    if (pinsLoading) return <div className="ncd-empty">Loading table pins…</div>;
    const q = search.trim().toLowerCase();
    // Newest pins first — the API returns them unsorted.
    const sorted = [...tablePins].sort((a, b) => {
      const ta = a?.createdDate ? new Date(a.createdDate).getTime() : 0;
      const tb = b?.createdDate ? new Date(b.createdDate).getTime() : 0;
      return tb - ta;
    });
    const pins = q
      ? sorted.filter(
          (p) =>
            String(p.tableNumber).toLowerCase().includes(q) ||
            String(p.pin).toLowerCase().includes(q)
        )
      : sorted;
    if (!pins.length)
      return (
        <div className="ncd-empty">
          <TableRestaurantOutlinedIcon sx={{ fontSize: "2.2rem", opacity: 0.4 }} />
          No table pins found
        </div>
      );
    return pins.map((p) => {
      const st = PIN_STATUS[p.status] || { label: p.status, bg: "#f1f5f9", color: "#475569" };
      return (
        <div key={p.sessionId || `${p.tableNumber}-${p.pin}`} className="ncd-card">
          <div className="ncd-card-avatar" style={{ background: st.bg, color: st.color }}>
            <TableRestaurantOutlinedIcon sx={{ fontSize: "1.3rem" }} />
          </div>
          <div className="ncd-card-main">
            <div className="ncd-card-chips">
              <span className="ncd-chip" style={{ color: st.color, background: st.bg }}>
                {st.label}
              </span>
              <span className="ncd-chip">{p.sessionActive ? "Session active" : "Inactive"}</span>
            </div>
            <h4 className="ncd-card-title">Table {p.tableNumber}</h4>
            <p className="ncd-card-desc">
              PIN <strong>{p.pin}</strong>
            </p>
          </div>
          <div className="ncd-card-time">
            <AccessTimeOutlinedIcon sx={{ fontSize: "0.85rem" }} /> {timeAgo(p.createdDate)}
          </div>
        </div>
      );
    });
  };

  const showReadPills = category !== "pins";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1400 }}
      PaperProps={{ className: "ncd-paper" }}
    >
      {/* Header */}
      <div className="ncd-header">
        <div className="ncd-header-title">
          <span className="ncd-title">Notifications</span>
          {unreadCount > 0 && <span className="ncd-title-badge">{unreadCount}</span>}
        </div>
        <div className="ncd-header-actions">
          <button
            type="button"
            className="ncd-btn ncd-btn-ghost"
            onClick={handleMarkAll}
            disabled={unreadCount === 0}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1.5 12 7 17 15 7" />
              <polyline points="7 12 12.5 17 20.5 7" />
            </svg>
            Mark all as read
          </button>
          <button type="button" className="ncd-btn ncd-btn-primary" onClick={handleSupport}>
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            Open support hub
          </button>
          <button type="button" className="ncd-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="ncd-stats">
        {stats.map((s) => (
          <button key={s.key} type="button" className="ncd-stat" onClick={s.onClick}>
            <div className="ncd-stat-text">
              <span className="ncd-stat-label">{s.label}</span>
              <span className="ncd-stat-value">{s.value}</span>
              <span className="ncd-stat-sub">{s.sub}</span>
            </div>
            <div className="ncd-stat-icon" style={{ background: s.bg, color: s.color }}>
              {s.icon}
            </div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="ncd-search">
        <SearchIcon sx={{ fontSize: "1.1rem", color: "#94a3b8" }} />
        <input
          className="ncd-search-input"
          placeholder="Search notifications, table no., order…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Tabs */}
      <div className="ncd-tabs-row">
        {categories.length > 0 && (
          <div className="ncd-seg">
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`ncd-seg-btn ${category === c.id ? "active" : ""}`}
                onClick={() => setCategory(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}
        {showReadPills && (
          <div className="ncd-pills">
            {["all", "unread", "read"].map((f) => (
              <button
                key={f}
                type="button"
                className={`ncd-pill ${readFilter === f ? "active" : ""}`}
                onClick={() => setReadFilter(f)}
              >
                {cap(f)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* List */}
      <div className="ncd-list">
        {category === "pins" ? (
          renderPins()
        ) : category === "tasks" && tasksLoading && !visibleList.length ? (
          <div className="ncd-empty">Loading table requests…</div>
        ) : visibleList.length === 0 ? (
          <div className="ncd-empty">
            <NotificationsNoneOutlinedIcon sx={{ fontSize: "2.2rem", opacity: 0.4 }} />
            {readFilter === "unread" ? "No unread notifications" : "No notifications"}
          </div>
        ) : (
          visibleList.map(renderCard)
        )}
      </div>
    </Drawer>
  );
};

export default NewNotificationDrawer;
